/**
 * お問合せ Hub の各タブ共通アクション。
 *
 * 機能:
 *   - 「お問合せ画面を開く」: 公開チャット URL を新しいタブで開く
 *   - 「埋め込みコード」: ダイアログで公開 URL / iframe スニペット / 完全版へのリンクを表示し、
 *     ワンクリックで clipboard へコピーできる
 *
 * 環境切替:
 *   VITE_FIREBASE_PROJECT_ID で prod/dev を判定し、対応する公開 URL を表示する。
 *   prod: https://caud-goenhub-inquiry.web.app
 *   dev:  https://caud-goenhub-dev-v2-inquiry.web.app
 *
 * 完全版（フローティングウィジェット）スニペットは長大なため、ここでは案内リンクのみ提供し、
 * 全文は docs/03_guides/caudesign_iframe_embed.md を参照する運用とする。
 */
import { Check, Code2, Copy, ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
const IS_PROD = PROJECT_ID === 'caud-goenhub';

const CHAT_URL = IS_PROD
  ? 'https://caud-goenhub-inquiry.web.app'
  : 'https://caud-goenhub-dev-v2-inquiry.web.app';

const ENV_LABEL = IS_PROD ? '本番 (prod)' : '開発 (dev-v2)';

/** ミニマル iframe スニペット（カスタマイズ不要、`</body>` 直前に貼るだけ）。 */
const MINIMAL_IFRAME_SNIPPET = `<iframe
  src="${CHAT_URL}/?embed=1"
  title="caudesign お問合せチャット"
  style="width:380px;height:620px;border:none;border-radius:14px;box-shadow:0 12px 36px rgba(0,0,0,0.28);"
  allow="clipboard-write"
  referrerpolicy="origin"
></iframe>`;

interface CopyableBlockProps {
  label: string;
  value: string;
  rows?: number;
  monospace?: boolean;
}

function CopyableBlock({ label, value, rows = 1, monospace = false }: CopyableBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} をコピーしました`);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error(`コピーに失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="!text-xs">{label}</Label>
        <Button size="sm" variant="secondary" onClick={handleCopy}>
          {copied ? (
            <>
              <Check className="size-3.5" /> コピー済
            </>
          ) : (
            <>
              <Copy className="size-3.5" /> コピー
            </>
          )}
        </Button>
      </div>
      <Textarea
        value={value}
        readOnly
        rows={rows}
        onFocus={(e) => e.currentTarget.select()}
        className={monospace ? 'font-mono text-[12px] leading-relaxed' : 'text-[13px]'}
      />
    </div>
  );
}

export function InquiryEmbedActions() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => window.open(CHAT_URL, '_blank', 'noopener,noreferrer')}
        title={`お問合せ画面を開く (${ENV_LABEL})`}
      >
        <ExternalLink className="size-4" />
        お問合せ画面を開く
      </Button>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Code2 className="size-4" />
        埋め込みコード
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>お問合せチャットの設置情報</DialogTitle>
            <DialogDescription>
              caudesign.jp や別サイトに設置するための公開 URL とコードです。 / 現在の環境: <strong>{ENV_LABEL}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <CopyableBlock label="① 公開 URL（テキストリンク等にそのまま）" value={CHAT_URL} rows={1} />

            <CopyableBlock
              label="② iframe スニペット（最小版 — </body> 直前に貼るだけ）"
              value={MINIMAL_IFRAME_SNIPPET}
              rows={7}
              monospace
            />

            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-xs leading-relaxed text-[var(--text-secondary)]">
              <div className="font-semibold text-[var(--text-primary)] mb-1">
                💡 フローティングウィジェット完全版が必要な場合
              </div>
              右下に💬ボタンを表示し、クリックで折り畳み可能なフル機能版のスニペットは
              <code className="mx-1 px-1 py-0.5 rounded bg-[var(--bg-surface)]">
                docs/03_guides/caudesign_iframe_embed.md
              </code>
              に記載しています（CSS + JS 込み、約 200 行）。caudesign.jp 本体に設置する際はそちらを利用してください。
            </div>

            {!IS_PROD && (
              <div className="rounded-md border border-[var(--accent)] bg-[var(--bg-elevated)] p-3 text-xs leading-relaxed">
                <strong>⚠️ 注意</strong>: 現在表示されているのは <strong>dev (dev-v2)</strong> 用の URL
                です。caudesign.jp 本体に貼るコードを取得したい場合は、本番 admin
                (https://caud-goenhub.web.app) を開いて同じ画面からコピーしてください。
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
